import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  Col,
  ColProps,
  Input,
  Row,
  notification,
  Dropdown,
  List,
  Divider,
  MenuProps,
  Modal,
} from 'antd';
import { DateTime } from 'luxon';
import { User } from '../../services/user-service/types';
import { Sprite } from '../sprite';
import { useEventStore } from '../../store/event-store';
import { Event } from '../../services/event-service/types';
import { useAuthStore } from '../../store/auth-store';
import { Role } from '../../services/auth-service/types';
import QRCodeGenerator from '../../components/qr-code';

/**
 * EditEventDetails props
 */
interface Props {}

interface EventForm {
  key?: string;
  required?: boolean;
  label?: string;
  colProps?: ColProps;
  render?: () => ReactElement;
  type?: string;
  placeholder?: string;
}

interface MultiCheckboxProps {
  options: string[];
  onChange: (values: string[]) => void;
  values: string[];
}

const MultiCheckbox = forwardRef<HTMLDivElement, MultiCheckboxProps>(
  ({ options, onChange, values }, ref) => {
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

    useEffect(() => {
      setSelectedOptions(values || []);
    }, [values]);

    return (
      <div
        ref={ref}
        className="p-4 bg-gray-200 border-slate-100 rounded-[10px] max-h-[250px] overflow-scroll">
        {options.map(option => (
          <p className="flex items-center space-x-2">
            <input
              type="checkbox"
              value={option}
              checked={
                values?.includes(option) || selectedOptions?.includes(option)
              }
              className="form-checkbox"
            />
            &nbsp;{option}
          </p>
        ))}
      </div>
    );
  },
);

/**
 * EditEventDetails Component
 */
const EditEventDetails: FunctionComponent<Props> = () => {
  const [api, contextHolder] = notification.useNotification();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  const customerUrl = String(window.location.origin).replace(
    'heartmap-admin',
    'heartmap-onsite',
  );

  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCodes, getDetailedEvent, biomarkers, deleteEvent, downloadCsv } =
    useEventStore();

  const { getValues, setValue, control } = useForm<Partial<Event>>({
    defaultValues: {},
  });

  useEffect(() => {
    setIsLoading(true);
    const fetchCodes = async () => {
      try {
        await getCodes();
        if (id) {
          const event = await getDetailedEvent(Number(id));
          if (event && biomarkers) {
            // eslint-disable-next-line array-callback-return
            const keys = Object.keys(event);
            keys.map(e => {
              if (e === 'biomarkers') {
                const transformedBiomarkers = event[e].map(code => {
                  const biomarker = biomarkers.find(bio => bio.code === code);
                  return `${biomarker?.code} - ${biomarker?.name}`;
                });
                // console.log(e, transformedBiomarkers);
                setValue(e, transformedBiomarkers);
              } else {
                setValue(e as keyof Event, event[e]);
              }
              return null;
            });
          }
        }
      } catch (error) {
        console.error('Error fetching codes:', error);
      }
      setIsLoading(false);
    };
    fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderField = useCallback(
    (fieldForm: EventForm) => (
      <Controller
        control={control}
        name={fieldForm.key as keyof Event}
        rules={{ required: fieldForm.required !== false }}
        render={({ field: fieldProps }) => {
          if (fieldForm.render) {
            return fieldForm.render();
          }
          if (fieldForm.type === 'date') {
            const { value } = fieldProps;
            return (
              <Input
                value={
                  value
                    ? new Date(String(value)).toISOString().split('T')[0]
                    : ''
                }
                className="border-none text-right"
              />
            );
          }
          if (fieldForm.type === 'multi-checkbox') {
            const { onChange, value } = fieldProps;
            const options = biomarkers.map(e => `${e.code} - ${e.name}`).sort();
            return (
              <MultiCheckbox
                {...fieldProps}
                values={value as string[]}
                options={[...new Set(options)]}
                onChange={val => {
                  onChange({ target: { value: val } });
                }}
              />
            );
          }
          return (
            <Input
              value={fieldProps.value?.toString() || ''}
              className="border-none text-right"
            />
          );
        }}
      />
    ),
    [biomarkers, control],
  );

  const creator = getValues('createdByUser') as User;
  const editor = getValues('updatedByUser') as User;

  const { idToken } = useAuthStore();
  const cUser = useMemo(() => idToken?.payload, [idToken]);

  const userRoles = useMemo<Role[]>(() => {
    if (!cUser?.['custom:roles']) return [];
    const roles = cUser['custom:roles'].split(',') as Role[];
    return roles;
  }, [cUser]);

  const canEditProfile = useMemo(() => {
    const roles = userRoles;
    const rolesAccess = [Role.SuperUser, Role.Admin];
    let canEdit = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canEdit = true;
      }
    });
    return canEdit;
  }, [userRoles]);

  const items: MenuProps['items'] = useMemo(
    () =>
      [
        {
          label: 'Edit Event',
          key: 'edit',
        },
        {
          label: 'Delete Event',
          key: 'delete',
        },
        {
          label: 'View QR',
          key: 'view-qr',
        },
        {
          label: 'Download Orders',
          key: 'download-orders',
        },
      ].filter(
        e =>
          (e.key === 'edit' && canEditProfile) ||
          (e.key === 'delete' && canEditProfile) ||
          (e.key !== 'delete' && e.key !== 'edit'),
      ),
    [userRoles],
  );

  const menuProps = {
    items,
    onClick: async e => {
      if (e.key === 'edit') {
        navigate(`/events/${id}/edit`);
      }
      if (e.key === 'delete') {
        setShowDeleteModal(true);
      }
      if (e.key === 'view-qr') {
        setShowQRModal(true);
      }
      if (e.key === 'download-orders') {
        try {
          await downloadCsv(Number(id));
        } catch(e) {
          api.error({ message: "Not available..." });
        }
      }
    },
  };

  const formFields = useMemo<EventForm[]>(
    () => [
      { key: 'name', label: 'Name', placeholder: 'eg. Pri Med West' },
      { key: 'slug', label: 'Slug', placeholder: 'eg. pri-med-west' },
      { key: 'orderPrefix', label: 'Order Prefix', placeholder: 'eg. PMW' },
      { key: 'biomarkers', label: 'Biomarkers', type: 'multi-checkbox' },
      { key: 'testCode', label: 'Test Code' },
      { key: 'clientId', label: 'Client ID' },
      { key: 'date', label: 'Event Date', type: 'date' },
    ],
    [],
  );

  const renderFormField = (formField, key) => {
    const field = formField[key];
    return <div className="pb-1">{renderField(field)}</div>;
  };

  const deleteEventCallback = useCallback(
    async (id: number) => {
      await deleteEvent({ id });
      navigate(`/events`);
    },
    [navigate],
  );

  return isLoading ? (
    <h3 className="text-2xl font-semibold mr-2 animate-pulse text-center">
      Loading...
    </h3>
  ) : (
    <>
      {contextHolder}
      <Card
        title={
          <div className="py-5">
            <Row gutter={10}>
              <Col>
                <Avatar
                  icon={
                    <Sprite
                      name="icon-drawer"
                      className="text-blue fill-bue py-3 px-3"
                    />
                  }
                  className="bg-white border-slate-100"
                  size={50}
                  gap={1}
                />
              </Col>
              <Col>
                <>
                  <h4 className="text-2xl font-semibold mr-2">
                    {getValues('name')}
                  </h4>
                  <div className="text-gray-500 text-xs font-normal">
                    Event No. {id}
                  </div>
                </>
              </Col>
            </Row>
          </div>
        }
        extra={
          <>
            {canEditProfile && (
              <Dropdown menu={menuProps} trigger={['click']}>
                <Button className="ml-2 bg-red-900 text-white border-red-900">
                  More Actions ▼
                </Button>
              </Dropdown>
            )}
          </>
        }>
        <Row>
          <Col span={11}>
            <div className="pb-1">
              <div className="py-[1px] flex flex-row">
                <Sprite
                  name="icon-drawer"
                  className="pt-[2px] w-[20px] h-[20px] text-blue fill-bue"
                />
                <div className="pl-[15px] text-[1rem]">Event Info</div>
              </div>
            </div>
            <List
              loading={false}
              bordered
              dataSource={formFields}
              renderItem={(item: EventForm, index: number) =>
                index !== 3 ? (
                  <List.Item key={index}>
                    <Col className="text-gray-500">{item.label}</Col>
                    <Col>{renderFormField(formFields, index)}</Col>
                  </List.Item>
                ) : null
              }
            />
          </Col>
          <Col>
            <Divider
              type="vertical"
              className="mx-[20px]"
              style={{ height: '100%' }}
            />
          </Col>

          <Col span={11}>
            <div className="pb-1">
              <div className="py-[1px] flex flex-row">
                <Sprite
                  name="icon-attachment"
                  className="pt-[2px] w-[20px] h-[20px] text-blue fill-bue"
                />
                <div className="pl-[15px] text-[1rem]">Biomarkers</div>
              </div>
            </div>
            <List
              loading={false}
              bordered
              // eslint-disable-next-line react/jsx-no-useless-fragment
              dataSource={[{ label: 'Biomarkers' }]}
              renderItem={(item: EventForm, index: number) => (
                <List.Item key={index}>
                  <Col className="text-gray-500">{item.label}</Col>
                  <Col>{renderFormField(formFields, 3)}</Col>
                </List.Item>
              )}
            />
            {creator && (
              <>
                <div className="pb-1 mt-5">
                  <div className="py-[1px] flex flex-row">
                    <Sprite
                      name="icon-person-admin"
                      className="pt-[2px] w-[20px] h-[20px] text-blue fill-bue"
                    />
                    <div className="pl-[15px] text-[1rem]">Authors</div>
                  </div>
                </div>
                <List bordered className="text-right">
                  {creator && (
                    <List.Item>
                      <Col className="text-gray-500">Created By</Col>
                      <Col>
                        {creator?.lastName || ''}, {creator?.firstName || ''}
                        <br />
                        <small>
                          {DateTime.fromISO(
                            getValues('createdAt') || '',
                          ).toFormat('LL-dd-yyyy hh:mm a') || ''}
                        </small>
                      </Col>
                    </List.Item>
                  )}
                  {editor && (
                    <List.Item>
                      <Col className="text-gray-500">Updated By</Col>
                      <Col>
                        {editor?.lastName || ''}, {editor?.firstName || ''}
                        <br />
                        <small>
                          {DateTime.fromISO(
                            getValues('updatedAt') || '',
                          ).toFormat('LL-dd-yyyy hh:mm a') || ''}
                        </small>
                      </Col>
                    </List.Item>
                  )}
                </List>
              </>
            )}
          </Col>
        </Row>
      </Card>
      <Modal
        title="Confirm Event Deletion"
        visible={showDeleteModal}
        onOk={() => {
          if (id) deleteEventCallback(+id);
        }}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        okText="Yes"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}>
        <p>{`Are you sure you want to delete event #${id}?`}</p>
        <i>{`Note: You can't reuse the slug '${getValues(
          'slug',
        )}' and the order prefix '${getValues(
          'orderPrefix',
        )}' again once it was deleted.`}</i>
      </Modal>
      <Modal
        title="QR Generator"
        visible={showQRModal}
        footer={null}
        onCancel={() => {
          setShowQRModal(false);
        }}>
        <div className="align-center">
          <QRCodeGenerator
            url={`${customerUrl}/registration/${getValues('slug')}`}
          />
        </div>
      </Modal>
    </>
  );
};

export default EditEventDetails;
